<script lang="ts">
export default {
  name: 'CustomerLayout',
};
</script>
<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';

import { useAuthStore } from '@/stores/auth.store';

import LoadingPanel from '@/components/LoadingPanel.vue';
import AppNavBar from '@/components/app-layouts/AppNavBar.vue';
import AppFooter from '@/components/app-layouts/AppFooter.vue';

import ImpersonationBar from '@/components/impersonation/ImpersonationBar.vue';
import PartnerTenantSideBar from '@/components/app-layouts/PartnerTenantSideBar.vue';
import { usePartnerTenantStore } from '@/stores/partnerTenant.storage';
import { isEqual } from 'lodash';
import { ROUTER } from '@/app-constants/router.constant';
import ScrollToTopButton from '@/components/app-layouts/ScrollToTopButton.vue';
import ChatWidget from '@/components/chat/ChatWidget.vue';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { hasAccess } from '@/utils/user.utils';
import { MODULES } from '@/app-constants/module.constants';
import { PERMISSIONS } from '@/app-constants/permission.constants';

const { hasAuthenticated } = useAuthStore();
const isAuthenticated = hasAuthenticated();
const partnerTenantStore = usePartnerTenantStore();
const router = useRouter();
const { params: routerParams } = router.currentRoute.value;

const { impersonatedUser } = useImpersonationInfo();

const hasChatAccess = computed(() =>
  hasAccess(impersonatedUser, {
    moduleName: MODULES.CHAT.moduleName,
    permission: PERMISSIONS.READ,
  }),
);

onMounted(() => {
  if (!partnerTenantStore.tenant) {
    // redirect to partner landing page
    router.push({ path: ROUTER.PARTNER.LANDING_PAGE.PATH });
  } else if (!isEqual(routerParams.tenantId, partnerTenantStore.tenant?.id)) {
    router.push({ path: `/partner/tenants/${partnerTenantStore.tenant?.id}` });
  }
});
</script>

<template>
  <template v-if="!isAuthenticated">
    <main class="relative h-full">
      <div class="h-full p-4">
        <LoadingPanel />
      </div>
    </main>
  </template>
  <template v-else>
    <div class="flex flex-col min-h-full">
      <ImpersonationBar />
      <AppNavBar :show-tenant-name="false" />
      <PartnerTenantSideBar />
      <main class="relative xl:ml-64 grow flex flex-col justify-between">
        <div class="p-4 xl:pl-5 xl:pr-8 grow">
          <RouterView />
        </div>
        <AppFooter />
      </main>
    </div>
    <ScrollToTopButton />
    <ChatWidget v-if="hasChatAccess" />
  </template>
</template>
